/**
 * Processor Balances — standalone page in the Finance DIRECT section (own data,
 * NOT QuickBooks; per Vishal 2026-07-31 the QuickBooks submenu is for QB raw
 * tables only). Funds payment processors hold that are not yet paid out, per
 * account: Shopify Payments (per store) + PayPal, via
 * GET /api/accounting/native/ar/processor-balances.
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatCurrencyPrecise, formatCompactCurrency } from '../../services/quickbooksService';
import arApNativeService from '../../services/arApNativeService';

export default function ProcessorBalances() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await arApNativeService.getProcessorBalances();
      if (res.success) setData(res.data);
      else setError('Failed to load processor balances.');
    } catch (err) {
      console.error('Error loading processor balances:', err);
      setError('Failed to load processor balances. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Processor Balances
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={loadData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Funds payment processors are holding that have not yet been paid out to the bank,
            per account. Covers Shopify Payments (per store), PayPal, and Amazon (the unsettled
            balance on each seller account&apos;s open settlement period, refreshed daily).
          </Typography>
          {data && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
              <Chip
                label={`Total ${formatCurrencyPrecise(data.total_reporting, data.reporting_currency)}`}
                color="primary"
              />
              {(data.totals_by_currency || []).map((t) => (
                <Chip
                  key={t.currency}
                  variant="outlined"
                  size="small"
                  label={`${t.currency} ${formatCurrencyPrecise(t.total_native, t.currency)} · ${t.accounts} account${t.accounts === 1 ? '' : 's'}`}
                />
              ))}
            </Box>
          )}
          {data && (data.unconvertible || []).length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Not included in the {data.reporting_currency} total — no exchange rate on file:{' '}
              {data.unconvertible
                .map((u) => `${u.currency} ${formatCurrencyPrecise(u.total_native, u.currency)}`)
                .join(' · ')}
            </Alert>
          )}
          {data && (data.history || []).length > 1 && (() => {
            const h = data.history;
            const vals = h.map((p) => Number(p.total_reporting) || 0);
            const min = Math.min(...vals);
            const span = (Math.max(...vals) - min) || 1;
            const pts = vals
              .map((v, i) => `${((i / (vals.length - 1)) * 196 + 2).toFixed(1)},${(28 - ((v - min) / span) * 24).toFixed(1)}`)
              .join(' ');
            return (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Total across processors, last {h.length} days ({h[0].date} {formatCompactCurrency(vals[0], data.reporting_currency)} → {h[h.length - 1].date} {formatCompactCurrency(vals[vals.length - 1], data.reporting_currency)})
                </Typography>
                <Box component="svg" width={200} height={30} viewBox="0 0 200 30" sx={{ color: 'primary.main', display: 'block' }}>
                  <polyline points={pts} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
                </Box>
              </Box>
            );
          })()}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Account</TableCell>
                  <TableCell>Processor</TableCell>
                  <TableCell>Currency</TableCell>
                  <TableCell align="right">Balance (native)</TableCell>
                  <TableCell align="right">In {data?.reporting_currency || 'USD'}</TableCell>
                  <TableCell>Snapshot</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data?.stores || []).map((s, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography fontWeight={500}>{s.connector_name}</Typography>
                      {/* One Amazon seller account spans several marketplace connectors, so the
                          connector name alone can't identify it — always show the account id. */}
                      {(s.source === 'amazon' || String(s.account_key || '').includes('.myshopify.com')) && (
                        <Typography variant="caption" color="text.secondary">
                          {s.source === 'amazon' ? `Seller account ${s.account_key}` : s.account_key}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.source === 'shopify' ? 'Shopify' : s.source === 'amazon' ? 'Amazon' : 'PayPal'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{s.currency}</TableCell>
                    <TableCell align="right">
                      <Typography fontWeight={600}>
                        {formatCurrencyPrecise(s.balance_native, s.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {s.fx_converted === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                          no {data?.reporting_currency} rate
                        </Typography>
                      ) : (
                        formatCurrencyPrecise(s.balance_reporting, data?.reporting_currency)
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{s.snapshot_date}</Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {!error && (!data || (data.stores || []).length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No processor balance snapshots for this company yet
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {data?.fx_note && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {data.fx_note}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
